import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

// SessionStorage keys
const SHAPES_STORAGE_KEY = 'architectural_shapes_data';
const SHAPE_CONFIG_STORAGE_KEY = 'architectural_shape_config';

// Utility functions for sessionStorage
const storageUtils = {
  saveShapesToStorage: (shapesData) => {
    try {
      const serializedData = JSON.stringify(shapesData);
      sessionStorage.setItem(SHAPES_STORAGE_KEY, serializedData);
    } catch (error) {
      console.error('Error saving shapes to sessionStorage:', error);
    }
  },

  loadShapesFromStorage: () => {
    try {
      const storedData = sessionStorage.getItem(SHAPES_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        // Validate the data structure
        if (Array.isArray(parsedData)) {
          return parsedData.map((shape) => ({
            ...shape,
            // Ensure required fields exist
            id: shape.id || Date.now().toString(),
            createdAt: shape.createdAt || new Date().toISOString(),
            color: shape.color || [255, 165, 0],
            coordinates: shape.coordinates || null,
            geometry: shape.geometry || null,
            lineMeasurements: shape.lineMeasurements || [],
            area: shape.area || 0,
            cumulativeLength: shape.cumulativeLength || 0,
            totalPerimeter: shape.totalPerimeter || 0,
          }));
        }
      }
      return [];
    } catch (error) {
      console.error('Error loading shapes from sessionStorage:', error);
      return [];
    }
  },

  saveConfigToStorage: (config) => {
    try {
      const serializedConfig = JSON.stringify(config);
      sessionStorage.setItem(SHAPE_CONFIG_STORAGE_KEY, serializedConfig);
    } catch (error) {
      console.error('Error saving config to sessionStorage:', error);
    }
  },

  loadConfigFromStorage: () => {
    try {
      const storedConfig = sessionStorage.getItem(SHAPE_CONFIG_STORAGE_KEY);
      if (storedConfig) {
        return JSON.parse(storedConfig);
      }
      return null;
    } catch (error) {
      console.error('Error loading config from sessionStorage:', error);
      return null;
    }
  },

  clearStorage: () => {
    try {
      sessionStorage.removeItem(SHAPES_STORAGE_KEY);
      sessionStorage.removeItem(SHAPE_CONFIG_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  },
};

const useShapeStore = create(
  subscribeWithSelector((set, get) => ({
    // Shape data - initialize from sessionStorage
    shapesData: storageUtils.loadShapesFromStorage(),
    selectedShapeIds: [],
    currentTool: 'polygon',
    isDrawing: false,
    isInitialized: false, // Track if store has been initialized

    // Configuration - initialize from sessionStorage
    selectedShapeType: null,
    selectedColor: null,
    shapeTypes: [],
    isLoadingShapeTypes: false,

    // UI state
    showLabelDialog: false,
    tempShape: null,
    shapeLabel: '',

    // Session management
    lastSaveTimestamp: Date.now(),

    // Actions
    initializeStore: () => {
      const config = storageUtils.loadConfigFromStorage();
      if (config) {
        set({
          selectedShapeType: config.selectedShapeType,
          selectedColor: config.selectedColor,
          shapeTypes: config.shapeTypes || [],
          currentTool: config.currentTool || 'polygon',
          isInitialized: true,
        });
      } else {
        set({ isInitialized: true });
      }
    },

    setShapes: (shapesData) => {
      set({ shapesData, lastSaveTimestamp: Date.now() });
      storageUtils.saveShapesToStorage(shapesData);
    },

    addShape: (shape) => {
      const newShapesData = [...get().shapesData, shape];
      set({
        shapesData: newShapesData,
        lastSaveTimestamp: Date.now(),
      });
      storageUtils.saveShapesToStorage(newShapesData);
    },

    updateShape: (shapeId, updates) => {
      const updatedShapesData = get().shapesData.map((shape) =>
        shape.id === shapeId ? { ...shape, ...updates, updatedAt: new Date().toISOString() } : shape
      );
      set({
        shapesData: updatedShapesData,
        lastSaveTimestamp: Date.now(),
      });
      storageUtils.saveShapesToStorage(updatedShapesData);
    },

    removeShape: (shapeId) => {
      const filteredShapes = get().shapesData.filter((shape) => shape.id !== shapeId);
      const filteredSelection = get().selectedShapeIds.filter((id) => id !== shapeId);

      set({
        shapesData: filteredShapes,
        selectedShapeIds: filteredSelection,
        lastSaveTimestamp: Date.now(),
      });
      storageUtils.saveShapesToStorage(filteredShapes);
    },

    removeShapes: (shapeIds) => {
      const filteredShapes = get().shapesData.filter((shape) => !shapeIds.includes(shape.id));
      const filteredSelection = get().selectedShapeIds.filter((id) => !shapeIds.includes(id));

      set({
        shapesData: filteredShapes,
        selectedShapeIds: filteredSelection,
        lastSaveTimestamp: Date.now(),
      });
      storageUtils.saveShapesToStorage(filteredShapes);
    },

    setSelectedShapeIds: (ids) => set({ selectedShapeIds: ids }),

    clearSelectedShapes: () => set({ selectedShapeIds: [] }),

    setCurrentTool: (tool) => {
      set({ currentTool: tool });
      // Save configuration
      const config = {
        selectedShapeType: get().selectedShapeType,
        selectedColor: get().selectedColor,
        shapeTypes: get().shapeTypes,
        currentTool: tool,
      };
      storageUtils.saveConfigToStorage(config);
    },

    setIsDrawing: (isDrawing) => set({ isDrawing }),

    setSelectedShapeType: (shapeType) => {
      set({ selectedShapeType: shapeType });
      // Save configuration
      const config = {
        selectedShapeType: shapeType,
        selectedColor: get().selectedColor,
        shapeTypes: get().shapeTypes,
        currentTool: get().currentTool,
      };
      storageUtils.saveConfigToStorage(config);
    },

    setSelectedColor: (color) => {
      set({ selectedColor: color });
      // Save configuration
      const config = {
        selectedShapeType: get().selectedShapeType,
        selectedColor: color,
        shapeTypes: get().shapeTypes,
        currentTool: get().currentTool,
      };
      storageUtils.saveConfigToStorage(config);
    },

    setShapeTypes: (types) => {
      set({ shapeTypes: types });
      // Save configuration
      const config = {
        selectedShapeType: get().selectedShapeType,
        selectedColor: get().selectedColor,
        shapeTypes: types,
        currentTool: get().currentTool,
      };
      storageUtils.saveConfigToStorage(config);
    },

    setIsLoadingShapeTypes: (loading) => set({ isLoadingShapeTypes: loading }),

    setShowLabelDialog: (show) => set({ showLabelDialog: show }),

    setTempShape: (shape) => set({ tempShape: shape }),

    setShapeLabel: (label) => set({ shapeLabel: label }),

    // Enhanced shape operations with storage persistence
    bulkUpdateShapes: (shapesWithUpdates) => {
      const updatedShapesData = get().shapesData.map((shape) => {
        const updateData = shapesWithUpdates.find((update) => update.id === shape.id);
        if (updateData) {
          return { ...shape, ...updateData.updates, updatedAt: new Date().toISOString() };
        }
        return shape;
      });

      set({
        shapesData: updatedShapesData,
        lastSaveTimestamp: Date.now(),
      });
      storageUtils.saveShapesToStorage(updatedShapesData);
    },

    // Complex operations
    getShapeById: (shapeId) => {
      return get().shapesData.find((shape) => shape.id === shapeId);
    },

    getSelectedShapes: () => {
      const { shapesData, selectedShapeIds } = get();
      return shapesData.filter((shape) => selectedShapeIds.includes(shape.id));
    },

    getShapesByType: (shapeType) => {
      return get().shapesData.filter((shape) => shape.shapeType === shapeType);
    },

    getShapesByTool: (tool) => {
      return get().shapesData.filter((shape) => shape.tool === tool);
    },

    getMeasurementSummary: () => {
      const { shapesData } = get();
      return {
        totalShapes: shapesData.length,
        totalArea: shapesData.reduce((sum, shape) => sum + (shape.area || 0), 0),
        totalLength: shapesData.reduce((sum, shape) => sum + (shape.cumulativeLength || 0), 0),
        totalPerimeter: shapesData.reduce((sum, shape) => sum + (shape.totalPerimeter || 0), 0),
        totalLineSegments: shapesData.reduce(
          (sum, shape) => sum + (shape.lineMeasurements?.length || 0),
          0
        ),
        shapesByType: shapesData.reduce((acc, shape) => {
          acc[shape.shapeType] = (acc[shape.shapeType] || 0) + 1;
          return acc;
        }, {}),
        shapesByTool: shapesData.reduce((acc, shape) => {
          acc[shape.tool] = (acc[shape.tool] || 0) + 1;
          return acc;
        }, {}),
      };
    },

    // Data validation and cleanup
    validateShapeData: (shape) => {
      const requiredFields = ['id', 'geometry', 'coordinates', 'tool', 'createdAt'];
      const missingFields = requiredFields.filter((field) => !shape[field]);

      if (missingFields.length > 0) {
        console.warn(`Shape ${shape.id} missing required fields:`, missingFields);
        return false;
      }

      return true;
    },

    cleanupInvalidShapes: () => {
      const validShapes = get().shapesData.filter((shape) => get().validateShapeData(shape));
      if (validShapes.length !== get().shapesData.length) {
        console.log(`Cleaned up ${get().shapesData.length - validShapes.length} invalid shapes`);
        set({ shapesData: validShapes, lastSaveTimestamp: Date.now() });
        storageUtils.saveShapesToStorage(validShapes);
      }
    },

    // Export/Import functionality
    exportShapesToJson: () => {
      const { shapesData } = get();
      const exportData = {
        shapes: shapesData,
        exportedAt: new Date().toISOString(),
        version: '1.0',
        totalShapes: shapesData.length,
        summary: get().getMeasurementSummary(),
      };
      return JSON.stringify(exportData, null, 2);
    },

    importShapesFromJson: (jsonString) => {
      try {
        const importData = JSON.parse(jsonString);
        if (importData.shapes && Array.isArray(importData.shapes)) {
          const validShapes = importData.shapes.filter((shape) => get().validateShapeData(shape));

          set({
            shapesData: validShapes,
            lastSaveTimestamp: Date.now(),
          });
          storageUtils.saveShapesToStorage(validShapes);

          return {
            success: true,
            importedCount: validShapes.length,
            totalCount: importData.shapes.length,
            skippedCount: importData.shapes.length - validShapes.length,
          };
        } else {
          throw new Error('Invalid import data format');
        }
      } catch (error) {
        console.error('Error importing shapes:', error);
        return {
          success: false,
          error: error.message,
        };
      }
    },

    // Reset functions
    resetDrawingState: () =>
      set({
        isDrawing: false,
        tempShape: null,
        shapeLabel: '',
        showLabelDialog: false,
      }),

    resetAll: () => {
      storageUtils.clearStorage();
      set({
        shapesData: [],
        selectedShapeIds: [],
        currentTool: 'polygon',
        isDrawing: false,
        tempShape: null,
        shapeLabel: '',
        showLabelDialog: false,
        selectedShapeType: null,
        selectedColor: null,
        shapeTypes: [],
        lastSaveTimestamp: Date.now(),
      });
    },

    resetShapesOnly: () => {
      const emptyShapes = [];
      storageUtils.saveShapesToStorage(emptyShapes);
      set({
        shapesData: emptyShapes,
        selectedShapeIds: [],
        lastSaveTimestamp: Date.now(),
      });
    },

    // Storage management
    getStorageStats: () => {
      try {
        const shapesData = sessionStorage.getItem(SHAPES_STORAGE_KEY);
        const configData = sessionStorage.getItem(SHAPE_CONFIG_STORAGE_KEY);

        return {
          shapesDataSize: shapesData ? shapesData.length : 0,
          configDataSize: configData ? configData.length : 0,
          totalSize: (shapesData?.length || 0) + (configData?.length || 0),
          shapeCount: get().shapesData.length,
          lastSaveTimestamp: get().lastSaveTimestamp,
          storageKeys: {
            shapes: SHAPES_STORAGE_KEY,
            config: SHAPE_CONFIG_STORAGE_KEY,
          },
        };
      } catch (error) {
        console.error('Error getting storage stats:', error);
        return null;
      }
    },

    // Force save current state
    forceSave: () => {
      storageUtils.saveShapesToStorage(get().shapesData);
      const config = {
        selectedShapeType: get().selectedShapeType,
        selectedColor: get().selectedColor,
        shapeTypes: get().shapeTypes,
        currentTool: get().currentTool,
      };
      storageUtils.saveConfigToStorage(config);
      set({ lastSaveTimestamp: Date.now() });
    },
  }))
);

// Subscribe to state changes and auto-save critical data
useShapeStore.subscribe(
  (state) => state.shapesData,
  (shapesData) => {
    // Auto-save shapes data whenever it changes
    storageUtils.saveShapesToStorage(shapesData);
  }
);

export default useShapeStore;
